import { PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { App, Button, Flex, Form, Input, Modal, Segmented, Select, Space, Table, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type ChannelPlatformName } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { ExportButton } from "@/components/list/ExportButton";
import { DateRangeFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { useActiveWarehouse, useWarehouses } from "@/lib/useWarehouses";
import { PLATFORMS, useConnections } from "../channels/hooks";
import { PlatformLabel } from "../channels/PlatformBadge";
import { CaseKindTag, CaseStatusTag, CreateCaseModal, KINDS, REASONS, RESOLUTIONS, RespondDeadline, type CaseKindAny, type CaseSummary, type OrderForCase } from "./common";
import { usePoll } from "@/components/tabs/WorkTabs";

const CASE_STATUSES = ["open", "receiving", "received", "resolved", "cancelled"] as const;

type Show = "respond" | "late" | "active" | "open" | "receiving" | "received" | "resolved" | "cancelled" | "all";

/**
 * Purna jual (Plan §6.10): every return, refund request, failed delivery,
 * complaint and exchange, from the marketplace, from CS or from the returns
 * dock. Active cases first: what still waits for goods or for a decision.
 */
export function Aftersales() {
  const { t, formatDateTime, formatNumber } = useI18n();
  const { me, can } = useAuth();
  const navigate = useNavigate();
  const tz = me?.workspace?.timezone;
  const [show, setShow] = useUrlFilter<Show>("show", "active", ["respond", "late", "active", "open", "receiving", "received", "resolved", "cancelled", "all"]);
  const [kind, setKind] = useUrlFilter<string>("kind", "");
  const [q, setQ] = useUrlFilter<string>("q", "");
  const [resolution, setResolution] = useUrlFilter<string>("resolution", "");
  const [platform, setPlatform] = useUrlFilter<ChannelPlatformName | "">("platform", "", ["", ...PLATFORMS]);
  const [connectionId, setConnectionId] = useUrlFilter<string>("store", "");
  const [pickedWarehouse, setWarehouseId] = useUrlFilter<string>("warehouse", "");
  const range = useDateRange();
  const page = useListPage();
  const connections = useConnections();
  const warehouses = useWarehouses();
  const activeWarehouse = useActiveWarehouse().current?.id ?? "";
  // Members limited to some warehouses see one at a time (Plan §6.14).
  const warehouseId = pickedWarehouse || (warehouses.restricted ? activeWarehouse : "");
  const [finding, setFinding] = useState(false);
  const [order, setOrder] = useState<OrderForCase | null>(null);
  const filters = {
    ...(show === "respond" ? { respond: true } : show === "late" ? { late: true } : show !== "all" ? { status: show } : {}),
    ...(kind ? { kind: kind as CaseKindAny } : {}),
    ...(q ? { q } : {}),
    ...(resolution ? { resolution: resolution as (typeof RESOLUTIONS)[number] } : {}),
    ...(platform ? { platform } : {}),
    ...(connectionId ? { connectionId } : {}),
    ...(warehouseId ? { warehouseId } : {}),
    ...range.query,
  };
  const list = useQuery({
    queryKey: ["aftersales", "list", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/aftersales", { params: { query: { ...filters, ...page.query } } })),
    refetchInterval: usePoll(30_000),
    placeholderData: keepPreviousData,
  });
  const counts = list.data?.counts;

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space orientation="vertical" size={0}>
          <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
            {t("as.title")}
          </Typography.Title>
          {counts && (
            <Typography.Text type="secondary">
              {t("as.counts", { open: formatNumber(counts.open), receiving: formatNumber(counts.receiving), received: formatNumber(counts.received) })}
              {counts.respond > 0 && (
                <Typography.Text type={counts.respondUrgent > 0 ? "danger" : "warning"}>
                  {" · "}
                  {t("as.countsRespond", { n: formatNumber(counts.respond), urgent: formatNumber(counts.respondUrgent) })}
                </Typography.Text>
              )}
            </Typography.Text>
          )}
        </Space>
        {can("orders.manage") && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setFinding(true)}>
            {t("as.add")}
          </Button>
        )}
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <Segmented<Show>
          value={show}
          onChange={setShow}
          options={[
            {
              value: "respond",
              label: (
                <span>
                  {t("as.filter.respond")}
                  {counts && counts.respond > 0 && <span className="z-num"> {formatNumber(counts.respond)}</span>}
                </span>
              ),
            },
            {
              value: "late",
              label: (
                <span>
                  {t("as.filter.late")}
                  {counts && counts.late > 0 && <span className="z-num"> {formatNumber(counts.late)}</span>}
                </span>
              ),
            },
            { value: "active", label: t("as.filter.active") },
            { value: "open", label: t("as.status.open") },
            { value: "receiving", label: t("as.status.receiving") },
            { value: "received", label: t("as.status.received") },
            { value: "resolved", label: t("as.status.resolved") },
            { value: "cancelled", label: t("as.status.cancelled") },
            { value: "all", label: t("orders.all") },
          ]}
        />
        <Select<string>
          value={kind}
          onChange={setKind}
          style={{ width: 200 }}
          options={[
            { value: "", label: t("as.filter.allKinds") },
            ...[...KINDS, "unidentified" as const].map((k) => ({ value: k, label: t(`as.kind.${k}`) })),
          ]}
        />
        <Input.Search allowClear defaultValue={q} placeholder={t("as.search")} onSearch={(v) => setQ(v.trim())} style={{ width: 340 }} />
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <Select<string>
          value={resolution}
          onChange={setResolution}
          style={{ width: 200 }}
          options={[{ value: "", label: t("as.filter.allResolutions") }, ...RESOLUTIONS.map((r) => ({ value: r, label: t(`as.resolution.${r}`) }))]}
        />
        <Select<ChannelPlatformName | "">
          value={platform}
          onChange={setPlatform}
          style={{ width: 170 }}
          options={[{ value: "", label: t("channels.colMarketplace") }, ...PLATFORMS.map((p) => ({ value: p, label: t(`platform.${p}`) }))]}
        />
        {can("channels.view") && (
          <Select<string>
            value={connectionId}
            onChange={setConnectionId}
            style={{ width: 200 }}
            showSearch={{ optionFilterProp: "label" }}
            options={[
              { value: "", label: t("orders.allStores") },
              ...(connections.data?.items ?? []).filter((c) => !platform || c.platform === platform).map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
        )}
        {(warehouses.accessible.length > 1 || (!warehouses.restricted && warehouses.active.length > 1)) && (
          <Select<string>
            value={warehouseId}
            onChange={setWarehouseId}
            style={{ width: 200 }}
            options={[
              ...(warehouses.restricted ? [] : [{ value: "", label: t("inventory.allWarehouses") }]),
              ...warehouses.accessible.map((w) => ({ value: w.id, label: w.name })),
            ]}
          />
        )}
        <DateRangeFilter range={range} />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/aftersales/export"
          query={filters}
          filename={t("as.title")}
          columns={[
            { key: "number", label: t("as.number") },
            { key: "kind", label: t("as.kindLabel"), values: Object.fromEntries([...KINDS, "unidentified" as const].map((k) => [k, t(`as.kind.${k}`)])) },
            { key: "source", label: t("as.sourceLabel"), values: Object.fromEntries((["marketplace", "manual", "warehouse"] as const).map((v) => [v, t(`as.source.${v}`)])) },
            { key: "status", label: t("common.status"), values: Object.fromEntries(CASE_STATUSES.map((v) => [v, t(`as.status.${v}`)])) },
            { key: "orderNumber", label: t("as.col.order") },
            { key: "platform", label: t("channels.colMarketplace"), values: Object.fromEntries(PLATFORMS.map((p) => [p, t(`platform.${p}`)])) },
            { key: "shopName", label: t("orders.store") },
            { key: "warehouseName", label: t("orders.warehouse") },
            { key: "reason", label: t("as.reasonLabel"), values: Object.fromEntries(REASONS.map((r) => [r, t(`as.reason.${r}`)])) },
            { key: "buyerReason", label: t("as.buyerReason") },
            { key: "returnTrackingNo", label: t("as.returnTracking") },
            { key: "expectedUnits", label: t("as.expectedUnits"), kind: "number" },
            { key: "receivedUnits", label: t("as.receivedUnits"), kind: "number" },
            { key: "resolution", label: t("as.resolutionLabel"), values: Object.fromEntries(RESOLUTIONS.map((r) => [r, t(`as.resolution.${r}`)])) },
            { key: "createdAt", label: t("as.col.created"), kind: "datetime" },
            { key: "receivedAt", label: t("as.receivedAt"), kind: "datetime" },
            { key: "resolvedAt", label: t("as.resolvedAt"), kind: "datetime" },
          ]}
        />
      </Flex>
      <Table<CaseSummary>
        rowKey="id"
        loading={list.isFetching && !list.data}
        dataSource={list.data?.items ?? []}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("as.empty") }}
        onRow={(x) => ({ onClick: () => navigate(`/app/aftersales/${x.id}`), style: { cursor: "pointer" } })}
        columns={[
          { title: t("as.number"), dataIndex: "number", width: 120, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          {
            title: t("as.kindLabel"),
            width: 150,
            render: (_, x) => (
              <Space orientation="vertical" size={2}>
                <CaseKindTag kind={x.kind} />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t(`as.source.${x.source}`)}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: t("common.status"),
            dataIndex: "status",
            width: 170,
            render: (s: CaseSummary["status"], x) => (
              <Space orientation="vertical" size={2}>
                <CaseStatusTag status={s} />
                {x.respondAction && <RespondDeadline at={x.respondBy} />}
              </Space>
            ),
          },
          {
            title: t("as.col.order"),
            render: (_, x) =>
              x.orderNumber ? (
                <Space orientation="vertical" size={0}>
                  <Typography.Text className="z-num">{x.orderNumber}</Typography.Text>
                  {x.platform && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      <PlatformLabel platform={x.platform as never} /> · {x.shopName}
                    </Typography.Text>
                  )}
                </Space>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: t("as.reasonLabel"),
            render: (_, x) => (x.reason ? t(`as.reason.${x.reason}`) : x.buyerReason || <Typography.Text type="secondary">—</Typography.Text>),
          },
          { title: t("as.returnTracking"), dataIndex: "returnTrackingNo", width: 160, render: (v: string) => (v ? <span className="z-num">{v}</span> : "—") },
          {
            title: t("as.col.units"),
            width: 130,
            align: "right",
            render: (_, x) => (
              <span className="z-num">
                {formatNumber(x.expectedUnits)} · {formatNumber(x.receivedUnits)}
              </span>
            ),
          },
          { title: t("as.col.created"), dataIndex: "createdAt", width: 170, render: (v: string) => formatDateTime(v, tz) },
        ]}
      />
      <FindOrder
        open={finding}
        onClose={() => setFinding(false)}
        onFound={(o) => {
          setFinding(false);
          setOrder(o);
        }}
      />
      <CreateCaseModal order={order} onClose={() => setOrder(null)} onCreated={(c) => (setOrder(null), navigate(`/app/aftersales/${c.id}`))} />
    </>
  );
}

/** Finds the order a new case is about, by its marketplace number. */
function FindOrder({ open, onClose, onFound }: { open: boolean; onClose: () => void; onFound: (o: OrderForCase) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const [form] = Form.useForm<{ number: string }>();
  const find = useMutation({
    mutationFn: async (number: string) => {
      const list = await unwrap(api.GET("/v1/orders", { params: { query: { q: number } } }));
      const hit = list.items.find((o) => o.externalId.toUpperCase() === number.toUpperCase()) ?? (list.items.length === 1 ? list.items[0] : undefined);
      if (!hit) return null;
      const d = await unwrap(api.GET("/v1/orders/{orderID}", { params: { path: { orderID: hit.id } } }));
      return { id: d.order.id, externalId: d.order.externalId, warehouseId: d.order.warehouseId, lines: d.lines } satisfies OrderForCase;
    },
    onSuccess: (o) => {
      if (!o) {
        form.setFields([{ name: "number", errors: [t("as.orderNotFound")] }]);
        return;
      }
      form.resetFields();
      onFound(o);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open={open}
      title={t("as.add")}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("common.continue")}
      cancelText={t("common.cancel")}
      confirmLoading={find.isPending}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={(v) => find.mutate(v.number.trim())} requiredMark={false}>
        <Form.Item name="number" label={t("as.findOrder")} extra={t("as.findOrderHelp")} rules={[{ required: true, whitespace: true }]}>
          <Input autoFocus className="z-num" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
